import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import { apiUrls } from 'shared/entities/domain';
import {
  NewsFeedItem,
  NewsFeedResponseServer,
  NewsViewedPair,
  normalizeNewsFeedItems
} from 'shared/newEntities/newsFeed';
import { IRootStore } from 'shared/entities/store/rootStore';
import { LoadingStageModel } from 'shared/models/loadingStage';

export default class NewsStore {
  readonly rootStore: IRootStore;
  readonly loadStage = new LoadingStageModel();

  private _items: NewsFeedItem[] = [];

  private loadAbortController: AbortController | null = null;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;

    makeObservable<NewsStore, '_items'>(this, {
      _items: observable.ref,

      items: computed,
      hasNews: computed,

      reset: action,
      loadFeed: action,
      markViewed: action
    });
  }

  get items(): NewsFeedItem[] {
    return this._items;
  }

  get hasNews(): boolean {
    return this.items.length > 0;
  }

  loadFeed = async (): Promise<void> => {
    if (this.loadStage.isLoading) {
      return;
    }

    this.loadStage.loading();
    const abortController = new AbortController();
    this.loadAbortController = abortController;
    const response =
      await this.rootStore.networkStore.api<NewsFeedResponseServer>(
        apiUrls.NEWS_FEED,
        {
          config: { signal: abortController.signal },
          showUnexpectedError: false
        }
      );

    if (
      abortController.signal.aborted ||
      abortController !== this.loadAbortController
    ) {
      return;
    }

    this.loadAbortController = null;

    if (response.isError) {
      this.loadStage.error();
      return;
    }

    const items = normalizeNewsFeedItems(response.data.items);

    runInAction(() => {
      this._items = items;
      this.loadStage.success();
    });
  };

  markViewed = (): void => {
    if (!this.hasNews) {
      return;
    }

    const items: NewsViewedPair[] = this.items.map((item) => ({
      news_id: item.id,
      test_send_seq: item.testSendSeq
    }));

    this._items = [];
    void this.rootStore.networkStore.api(apiUrls.NEWS_MARK_VIEWED, {
      method: 'POST',
      data: { items },
      showUnexpectedError: false
    });
  };

  reset = (): void => {
    this.cancelLoad();
    this._items = [];
    this.loadStage.reset();
  };

  private cancelLoad = (): void => {
    this.loadAbortController?.abort();
    this.loadAbortController = null;
  };
}
